import { EWalletId, ProviderEvents } from '@ankr.com/provider';
import { createListenerMiddleware } from '@reduxjs/toolkit';

import { MultiService } from 'modules/api/MultiService';
import { NewProjectStep } from 'domains/projects/types';
import { RootState } from 'store';
import { deleteJwtToken } from 'domains/jwtToken/action/deleteJwtToken';
import { getProviderManager } from 'modules/api/getProviderManager';
import { initAuthListeners } from 'modules/auth/store/listeners';
import { initTFAListeners } from 'modules/tfa/store/listeners';
import { initWalletListeners } from 'modules/wallet/store/listeners';
import { isEventProvider } from 'store/utils/isEventProvider';
import { resetConfig, selectNewProjectConfig } from 'domains/projects/store';
import { resetOriginChainURL } from 'domains/chains/store/chainsSlice';
import { selectAccountAddress } from 'modules/account/store/selectors';
import { signOut } from 'modules/auth/actions/signOut';

export const listenerMiddleware = createListenerMiddleware<RootState>();

initAuthListeners({ listenerMiddleware });

initTFAListeners({ listenerMiddleware });

initWalletListeners({ listenerMiddleware });

listenerMiddleware.startListening({
  matcher: signOut.matchFulfilled,
  effect: async () => {
    const service = MultiService.getWeb3Service();

    if (service) {
      const providerManager = getProviderManager();
      const ethWeb3KeyProvider = await providerManager.getETHWriteProvider(
        EWalletId.injected,
      );
      const provider = ethWeb3KeyProvider.getWeb3()?.currentProvider;

      if (isEventProvider(provider)) {
        provider.removeAllListeners(ProviderEvents.Disconnect);
        provider.removeAllListeners(ProviderEvents.ChainChanged);
      }
    }

    MultiService.removeServices();
  },
});

listenerMiddleware.startListening({
  matcher: signOut.matchFulfilled,
  effect: async (_action, { cancelActiveListeners, dispatch }) => {
    cancelActiveListeners();

    dispatch(resetOriginChainURL());
  },
});

listenerMiddleware.startListening({
  matcher: deleteJwtToken.matchFulfilled,
  effect: async (
    {
      meta: {
        arg: { originalArgs = {} },
      },
    },
    { dispatch, getState },
  ) => {
    const deletedTokenIndex = originalArgs?.params?.tokenIndex;

    const { project } = selectNewProjectConfig(getState() as RootState);
    const newProjectTokenIndex = project?.[NewProjectStep.General]?.tokenIndex;

    if (deletedTokenIndex === newProjectTokenIndex) {
      const address = selectAccountAddress(getState() as RootState) ?? '';

      dispatch(resetConfig(address));
    }
  },
});
